import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../utils/echarts'
import type { TaxRateComparisonEntry } from '@popety-io/types'

interface RateDistributionStripProps {
  rates: TaxRateComparisonEntry[]
  currentName: string
  highlightColor: string
  currentRate: number
}

interface GroupedRate {
  rate: number
  names: string[]
  isHighlighted: boolean
}

const groupByRate = (
  rates: TaxRateComparisonEntry[],
  currentName: string
): { others: GroupedRate[]; highlighted: GroupedRate | null } => {
  const map = new Map<number, string[]>()
  for (const d of rates) {
    const existing = map.get(d.rate) || []
    existing.push(d.name)
    map.set(d.rate, existing)
  }

  let highlighted: GroupedRate | null = null
  const others: GroupedRate[] = []

  for (const [rate, names] of map) {
    const hasCurrent = names.includes(currentName)
    if (hasCurrent) {
      highlighted = { rate, names, isHighlighted: true }
    }
    const otherNames = names.filter((n) => n !== currentName)
    if (otherNames.length > 0) {
      others.push({ rate, names: otherNames, isHighlighted: false })
    }
  }

  return { others, highlighted }
}

export const RateDistributionStrip: React.FC<RateDistributionStripProps> = ({
  rates,
  currentName,
  highlightColor,
  currentRate,
}) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useEffect(() => {
    if (!chartRef.current || rates.length === 0) return

    chartInstance.current = safeChartInit(chartRef.current)

    const { others, highlighted } = groupByRate(rates, currentName)

    const maxCount = Math.max(...others.map((g) => g.names.length), 1)
    const minSize = 6
    const maxSize = 18
    const sizeFor = (count: number) =>
      minSize + (maxSize - minSize) * Math.sqrt(count / maxCount)

    const othersData = others.map((g) => ({
      value: [g.rate, 0] as [number, number],
      names: g.names,
      count: g.names.length,
      symbolSize: sizeFor(g.names.length),
    }))

    const highlightedData = highlighted
      ? [{ value: [highlighted.rate, 0] as [number, number] }]
      : []

    const allRates = rates.map((d) => d.rate)
    const dataMin = Math.min(...allRates)
    const dataMax = Math.max(...allRates)
    const padding = Math.max((dataMax - dataMin) * 0.08, 1)
    const axisMin = Math.floor(dataMin - padding)
    const axisMax = Math.ceil(dataMax + padding)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const data = params.data
          if (data?.names) {
            const names: string[] = data.names
            const rate = data.value[0]
            const count = names.length
            const maxShow = 10
            const lines = names.slice(0, maxShow).join('<br/>')
            const more =
              count > maxShow ? `<br/><i>+${count - maxShow} more</i>` : ''
            return `<strong>${rate}%</strong> (${count})<br/>${lines}${more}`
          }
          return `<strong>${currentName}</strong><br/>${currentRate}%`
        },
      },
      grid: { left: 0, right: 0, top: 20, bottom: 20, containLabel: false },
      xAxis: {
        type: 'value',
        min: axisMin,
        max: axisMax,
        axisLabel: { fontSize: 10, formatter: '{value}%', color: '#9ca3af' },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        show: false,
        min: -1,
        max: 1,
      },
      series: [
        {
          type: 'scatter',
          data: othersData,
          symbolSize: (val: number[], params: any) => {
            return params.data?.symbolSize ?? minSize
          },
          itemStyle: { color: '#d1d5db', opacity: 0.6 },
          emphasis: {
            itemStyle: { color: '#9ca3af', opacity: 1 },
            scale: 1.3,
          },
          z: 1,
        },
        {
          type: 'scatter',
          data: highlightedData,
          name: currentName,
          symbolSize: 14,
          itemStyle: { color: highlightColor },
          label: {
            show: true,
            position: 'top',
            formatter: `${currentRate}%`,
            fontSize: 10,
            fontWeight: 'bold' as const,
            color: highlightColor,
            distance: 4,
          },
          z: 2,
        },
      ],
    }

    chartInstance.current.setOption(option)

    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [rates, currentName, highlightColor, currentRate])

  if (rates.length === 0) return null

  return <div ref={chartRef} style={{ width: '100%', height: '70px' }} />
}
